import { useState, useRef } from 'react';
import { Landmark, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogCloseButton,
} from '../ui/dialog';
import { DashboardCard } from '../dashboard/dashboard-card';
import { BtcCaixinhaCard } from './btc-caixinha-card';
import { CaixinhaCard } from './caixinha-card';
import { CaixinhaForm } from './caixinha-form';
import { useAppData } from '../../contexts/app-data-context';
import { useDate } from '../../contexts/date-context';
import { formatCurrency } from '../../lib/format';
import { getCaixinhaCurrentValue, getTotalBtc } from '../../lib/caixinhas';
import type { Caixinha } from '../../types';

export function CaixinhaSection() {
  const {
    caixinhas,
    addCaixinha,
    updateCaixinha,
    deleteCaixinha,
    addSnapshot,
    updateSnapshot,
    deleteSnapshot,
    addCaixinhaLog,
    updateCaixinhaLog,
    deleteCaixinhaLog,
    btcApplications,
    addBtcApplication,
    updateBtcApplication,
    deleteBtcApplication,
    bitcoinPrice,
  } = useAppData();

  const { month, year } = useDate();
  const endOfMonth = new Date(year, month, 0, 23, 59, 59, 999);
  const totalCurrentValue = caixinhas.reduce((sum, c) => {
    const v = getCaixinhaCurrentValue(c, endOfMonth);
    return sum + (v ?? 0);
  }, 0);

  const totalBtc = getTotalBtc(btcApplications);
  const btcBrl = bitcoinPrice ? totalBtc * bitcoinPrice.brl : 0;
  const grandTotal = totalCurrentValue + btcBrl;

  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current = e.pageX - (scrollRef.current?.offsetLeft ?? 0);
    scrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = x - startX.current;
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCaixinha, setEditingCaixinha] = useState<Caixinha | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [caixinhaToDelete, setCaixinhaToDelete] = useState<Caixinha | null>(
    null
  );

  const handleFormSubmit = async (data: {
    title: string;
    description?: string;
  }) => {
    if (editingCaixinha) {
      await updateCaixinha(editingCaixinha.id, data);
    } else {
      await addCaixinha(data);
    }
    setDialogOpen(false);
    setEditingCaixinha(null);
  };

  const handleEdit = (c: Caixinha) => {
    setEditingCaixinha(c);
    setDialogOpen(true);
  };

  const handleDeleteRequest = (c: Caixinha) => {
    setCaixinhaToDelete(c);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (caixinhaToDelete) {
      await deleteCaixinha(caixinhaToDelete.id);
      setCaixinhaToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <>
      <DashboardCard
        title="Caixinhas"
        count={caixinhas.length}
        icon={<Plus />}
        iconLabel="Nova caixinha"
        onIconClick={() => {
          setEditingCaixinha(null);
          setDialogOpen(true);
        }}
        description={
          caixinhas.length === 0
            ? undefined
            : `Total: R$ ${formatCurrency(grandTotal)}`
        }
        collapsible
        className="overflow-hidden"
      >
        {caixinhas.length === 0 ? (
          <div className="flex w-full min-w-0 gap-3 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
            <div className="min-w-72 shrink-0">
              <BtcCaixinhaCard
                applications={btcApplications}
                onAdd={addBtcApplication}
                onUpdate={updateBtcApplication}
                onDelete={deleteBtcApplication}
              />
            </div>
            <div className="min-w-72 shrink-0 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border py-8 text-center">
              <Landmark className="size-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">Nenhuma caixinha criada</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setEditingCaixinha(null); setDialogOpen(true); }}
              >
                <Plus className="size-4" />
                Criar caixinha
              </Button>
            </div>
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="flex w-full min-w-0 gap-3 overflow-x-auto [&::-webkit-scrollbar]:hidden"
            style={{ cursor: 'grab', scrollbarWidth: 'none' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <div className="min-w-72 shrink-0 select-none">
              <BtcCaixinhaCard
                applications={btcApplications}
                onAdd={addBtcApplication}
                onUpdate={updateBtcApplication}
                onDelete={deleteBtcApplication}
              />
            </div>
            {caixinhas.map((c) => (
              <div key={c.id} className="min-w-72 shrink-0 select-none">
                <CaixinhaCard
                  caixinha={c}
                  onEdit={handleEdit}
                  onDelete={handleDeleteRequest}
                  onAddSnapshot={addSnapshot}
                  onUpdateSnapshot={updateSnapshot}
                  onDeleteSnapshot={deleteSnapshot}
                  onAddLog={addCaixinhaLog}
                  onUpdateLog={updateCaixinhaLog}
                  onDeleteLog={deleteCaixinhaLog}
                />
              </div>
            ))}
          </div>
        )}
      </DashboardCard>

      {/* Create / Edit dialog */}
      <DialogRoot
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false);
            setEditingCaixinha(null);
          }
        }}
      >
        <DialogPortal>
          <DialogBackdrop />
          <DialogPopup className="max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {editingCaixinha ? 'Editar caixinha' : 'Nova caixinha'}
              </DialogTitle>
              <DialogCloseButton />
            </DialogHeader>
            <DialogContent>
              <CaixinhaForm
                onSubmit={handleFormSubmit}
                onClose={() => {
                  setDialogOpen(false);
                  setEditingCaixinha(null);
                }}
                initialData={editingCaixinha}
              />
            </DialogContent>
          </DialogPopup>
        </DialogPortal>
      </DialogRoot>

      {/* Delete confirmation dialog */}
      <DialogRoot
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDialogOpen(false);
            setCaixinhaToDelete(null);
          }
        }}
      >
        <DialogPortal>
          <DialogBackdrop />
          <DialogPopup className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Confirmar exclusão</DialogTitle>
              <DialogCloseButton />
            </DialogHeader>
            <DialogContent>
              <p className="text-sm text-foreground-subtle">
                Tem certeza que deseja excluir a caixinha{' '}
                <strong className="text-foreground">
                  {caixinhaToDelete?.title}
                </strong>
                ? Todos os snapshots e movimentações serão perdidos.
              </p>
              <div className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setDeleteDialogOpen(false);
                    setCaixinhaToDelete(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleDeleteConfirm}
                >
                  Excluir
                </Button>
              </div>
            </DialogContent>
          </DialogPopup>
        </DialogPortal>
      </DialogRoot>
    </>
  );
}
